// =====================================================================
// Kanonischer Appkatalog
// =====================================================================
// Gemeinsame, oeffentliche Metadaten fuer Browser, Instanzfilter und
// serverseitige Registry-Auswahl. Dieser Katalog enthaelt bewusst keine
// Server-URLs, Umgebungsvariablen, Cookies oder Secrets.
// =====================================================================

import type { InstanceConfig } from "./instance.js";

export interface AppCatalogEntry {
  /** Stabiler DB-/SSO-/Registry-Claim. */
  key: string;
  /** Nutzerseitige Bezeichnung. */
  label: string;
  /** Historische UI-IDs, die nur bei der Aufloesung toleriert werden. */
  aliases?: readonly string[];
}

/**
 * Alle aktuell registrierten Satellite-Claims in kanonischer Schreibweise.
 * Stillgelegte Apps (jahresgespraeche, profil, skillpath, ga-cases) gehoeren
 * absichtlich nicht in diesen Katalog.
 */
export const APP_CATALOG: readonly AppCatalogEntry[] = [
  { key: "notes", label: "Nexus" },
  { key: "liquid", label: "Liquid" },
  { key: "money", label: "Money" },
  { key: "vertrag", label: "Vertrag" },
  { key: "briefing", label: "Briefing" },
  { key: "life", label: "Life" },
  { key: "scan", label: "Scan" },
  { key: "fair-kapa", label: "FAIR-Kapa", aliases: ["fair"] },
  { key: "back-office", label: "BackOffice" },
  { key: "regelwerke", label: "Regelwerke" },
  { key: "coorconn-liquid", label: "CoorConn Liquid" },
  { key: "story", label: "Story" },
  { key: "time", label: "Time" },
] as const;

export const REGISTERED_APP_KEYS = APP_CATALOG.map((entry) => entry.key) as readonly string[];

/**
 * Loest kanonische Claims und historische UI-IDs auf. Unbekannte Werte werden
 * lediglich normalisiert zurueckgegeben, damit Aufrufer sie sicher ablehnen
 * oder als eigenen Fallback darstellen koennen.
 */
export function normalizeAppKey(key: string): string {
  const normalized = key.trim().toLowerCase();
  const entry = APP_CATALOG.find((candidate) =>
    candidate.key === normalized || candidate.aliases?.includes(normalized),
  );
  return entry?.key ?? normalized;
}

/** Gibt den kanonischen Anzeigenamen zurueck, sonst den expliziten Fallback oder den Claim. */
export function getAppLabel(key: string, fallback?: string): string {
  const normalized = normalizeAppKey(key);
  return APP_CATALOG.find((entry) => entry.key === normalized)?.label ?? fallback ?? key;
}

/**
 * Entscheidet ausschliesslich anhand der Instanzkonfiguration, ob ein aktuell
 * registrierter App-Claim in dieser Instanz aktiv ist. `null` bewahrt den
 * Legacy-Standard: alle aktuell registrierten Apps sind aktiviert.
 */
export function isAppEnabledForInstance(
  key: string,
  config: Pick<InstanceConfig, "satellites">,
): boolean {
  const normalized = normalizeAppKey(key);
  if (!REGISTERED_APP_KEYS.includes(normalized)) return false;
  if (config.satellites === null) return true;
  return config.satellites.some((configured) => normalizeAppKey(configured) === normalized);
}
